import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { NumberFormatter } from '@repo/toolkit/number'
import { StringFormatter } from '@repo/toolkit/string'
import Badge from '@repo/ui/badge'
import Checkbox from '@repo/ui/checkbox'
import { Column } from '@repo/ui/data-table'
import { DebouncedInput } from '@repo/ui/debounced-input'
import { BagIcon, CheckMarkIcon, CreditCardIcon, PendingIcon } from '@repo/ui/icons'
import { TFunction } from 'i18next'

import { MissionReportStatusEnum } from '../../../lib/types/enums/mission/mission-report-status.enum'
import { staffPositionEnumMapping } from '../../../lib/types/enums/staff/staff-position.enum'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import { MissionTrackingSalary } from '../../../lib/types/models/mission-tracking/mission-tracking'
import { getIbanBadgeColors } from '../../../utils/functions/get-iban-badge-color'
import SalaryPaymentStatus from '../../elements/salary-payment-status'

type ColumnExtraAttributes = {
  version: RoleEnum[]
}

type MissionTrackingSalaryColumn = Column<MissionTrackingSalary> & ColumnExtraAttributes

const missionTrackingSalaryColumn = ({
  t,
  handlePasBlockedChange,
  handlePasChange
}: {
  t: TFunction<AvailableLanguages, undefined>
  handlePasBlockedChange?: (checked: boolean, missionTracking: MissionTrackingSalary) => void
  handlePasChange?: (pas: number | null, missionTracking: MissionTrackingSalary) => void
}): MissionTrackingSalaryColumn[] => [
  {
    header: '#',
    accessorKey: 'id',
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN],
    cell: ({ row, table }) => {
      const { pageIndex, pageSize } = table.getState().pagination
      return pageIndex * pageSize + row.index + 1
    }
  },
  {
    header: 'Nom',
    accessorKey: 'staff.lastName',
    enableSorting: true,
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN],
    cell: ({ row }) => row.original.staff.lastName.toUpperCase()
  },
  {
    header: 'Prénom',
    accessorKey: 'staff.firstName',
    enableSorting: true,
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN],
    cell: ({ row }) => StringFormatter.capitalize(row.original.staff.firstName)
  },
  {
    header: 'Poste',
    accessorKey: 'staff.position',
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN],
    cell: ({ row }) => t(staffPositionEnumMapping[row.original.staff.position])
  },
  {
    header: 'Date contrat',
    accessorKey: 'startDate',
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN],
    cell: ({ row }) => {
      const startDate = row.original.startDate
      const endDate = row.original.endDate
      if (!startDate || !endDate) return '-'
      return <Badge text={DateFormatter.getDateRangeDDMMYYYY(startDate, endDate)} />
    }
  },
  {
    header: '# Missions',
    accessorKey: 'viewData.missionsCount',
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN],
    cell: ({ row }) => <div className="w-20 text-center">{row.original.missionsCount}</div>
  },
  {
    header: 'Missions OK',
    accessorKey: 'affectedStaffs.missionCompleted',
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN],
    cell: ({ row }) => {
      const missionsCompleted = row.original.affectedStaffs.every(
        (mission) => mission.missionCompleted === true
      )
      return missionsCompleted ? (
        <Badge
          variant="success"
          icon={<CheckMarkIcon color={tailwindColors.green.DEFAULT} width="20px" height="20px" />}
        />
      ) : (
        <Badge
          variant="danger"
          icon={<PendingIcon color={tailwindColors.red.DEFAULT} width="20px" height="20px" />}
        />
      )
    }
  },

  {
    header: 'Honoraires',
    accessorKey: 'affectedStaffs.pay',
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN],
    cell: ({ row }) => {
      return (
        <Badge
          variant="primary"
          text={NumberFormatter.formatCurrency(
            (row.original.totalSalary ?? 0) - (row.original.exceptionalBonusAmount ?? 0)
          )}
        />
      )
    }
  },
  {
    header: 'Prime exceptionnelle',
    accessorKey: 'exceptionalBonusAmount',
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN],
    cell: ({ row }) => {
      const exceptionalBonusAmount = row.original.exceptionalBonusAmount
      const reason = row.original.exceptionalBonusReason
      if (!exceptionalBonusAmount) return '-'
      if (reason)
        return (
          <Badge
            variant="primary"
            text={`${reason} - ${NumberFormatter.formatCurrency(exceptionalBonusAmount)}`}
          />
        )
      return (
        <Badge variant="primary" text={NumberFormatter.formatCurrency(exceptionalBonusAmount)} />
      )
    }
  },
  {
    header: 'Salaire net avant PAS',
    accessorKey: 'viewData.totalSalary',
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN],
    cell: ({ row }) => {
      return (
        <div className="min-w-28">
          <Badge
            variant="primary"
            text={NumberFormatter.formatCurrency(row.original.totalSalary ?? 0)}
          />
        </div>
      )
    }
  },
  {
    header: 'Montant du PAS',
    accessorKey: 'pas',
    version: [RoleEnum.ACCOUNTANT],
    cell: ({ row }) => {
      const { pas, pasBlocked, id } = row.original
      if (pasBlocked)
        return pas != null ? (
          <Badge variant="primary" text={NumberFormatter.formatCurrency(pas)} />
        ) : (
          '-'
        )
      return (
        <DebouncedInput
          key={`pas-${id}-${pas}`}
          label="PAS"
          type="number"
          decimalPlaces={2}
          value={pas}
          onChange={(e) =>
            handlePasChange?.(e.target.value === '' ? null : Number(e.target.value), row.original)
          }
        />
      )
    }
  },
  {
    header: 'Montant du PAS',
    accessorKey: 'pas',
    version: [RoleEnum.ADMIN],
    cell: ({ row }) =>
      row.original.pas != null ? (
        <Badge variant="primary" text={NumberFormatter.formatCurrency(row.original.pas)} />
      ) : (
        '-'
      )
  },
  {
    header: 'Salaire net après PAS',
    accessorKey: 'viewData.totalSalaryAfterPas',
    version: [RoleEnum.ACCOUNTANT, RoleEnum.ADMIN],
    cell: ({ row }) => {
      return (
        <div className="min-w-28">
          <Badge
            variant="primary"
            text={NumberFormatter.formatCurrency(row.original.totalSalaryAfterPas ?? 0)}
          />
        </div>
      )
    }
  },
  {
    header: 'Ok',
    accessorKey: 'pasBlocked',
    version: [RoleEnum.ADMIN],
    cell: ({ row }) => (
      <Checkbox
        checked={row.original.pasBlocked}
        onChange={(checked) => handlePasBlockedChange?.(checked, row.original)}
      />
    )
  },
  {
    header: 'Rapport',
    accessorKey: 'affectedStaffs.reportStatus',
    version: [RoleEnum.ADMIN],
    cell: ({ row }) => {
      const hasAllReports = row.original.affectedStaffs.every(
        (mission) =>
          mission.reportStatus === MissionReportStatusEnum.VALIDATED ||
          mission.reportStatus === MissionReportStatusEnum.SENT
      )

      return (
        <Badge
          icon={
            <BagIcon
              color={hasAllReports ? tailwindColors.green.DEFAULT : tailwindColors.red.DEFAULT}
            />
          }
          iconPosition="start"
          color={
            hasAllReports ? tailwindColors.green.light.DEFAULT : tailwindColors.red.light.DEFAULT
          }
          size="flexible"
          className="rounded"
        />
      )
    }
  },
  {
    header: 'Banque',
    accessorKey: 'staff.ibanStatus',
    version: [RoleEnum.ADMIN],
    cell: ({ row }) => {
      const { ibanDetailsFilled, ibanExportedAt } = row.original.staff
      const { color, bgColor } = getIbanBadgeColors(ibanDetailsFilled, ibanExportedAt)
      return (
        <Badge
          icon={<CreditCardIcon color={color} />}
          iconPosition="start"
          color={bgColor}
          size="flexible"
          className="rounded"
        />
      )
    }
  },

  {
    header: 'Statut du paiement',
    accessorKey: 'paymentStatus',
    enableSorting: true,
    version: [RoleEnum.ADMIN],
    cell: ({ row }) => {
      const { paymentStatus, paymentValidatedAt, paymentExportedAt } = row.original
      return (
        <SalaryPaymentStatus
          paymentStatus={paymentStatus}
          paymentValidatedAt={paymentValidatedAt}
          paymentExportedAt={paymentExportedAt}
        />
      )
    }
  }
]

type GetMissionTrackingSalaryColumnsParams = {
  role: RoleEnum
  t: TFunction<AvailableLanguages, undefined>
  handlePasBlockedChange?: (checked: boolean, missionTracking: MissionTrackingSalary) => void
  handlePasChange?: (pas: number | null, missionTracking: MissionTrackingSalary) => void
}

export function getMissionTrackingSalaryColumns(params: GetMissionTrackingSalaryColumnsParams) {
  return missionTrackingSalaryColumn({
    t: params.t,
    handlePasBlockedChange: params.handlePasBlockedChange,
    handlePasChange: params.handlePasChange
  }).filter((column) => column.version.includes(params.role))
}
